import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AdvancedCommerceState } from './advancedAssessmentApi';
import { trackAdvancedPurchase } from './assessmentAnalytics';

const Card = styled.section`
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(24px, 5vw, 48px);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  background: white;
  box-shadow: var(--shadow-sm);

  h1 { margin: 0 0 12px; color: var(--color-accent-dark); font-size: clamp(1.7rem, 4vw, 2.5rem); }
  p { color: var(--text-secondary); line-height: 1.6; }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;

  button {
    min-height: 48px;
    padding: 12px 18px;
    border: 1px solid var(--color-accent-primary);
    border-radius: 8px;
    background: var(--color-accent-primary-fill);
    color: white;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
  }
  button + button { background: white; color: var(--color-accent-dark); }
  button:disabled { cursor: not-allowed; opacity: 0.6; }
  button:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.4); outline-offset: 3px; }

  a {
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    padding: 12px 18px;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    color: var(--color-accent-dark);
    font-weight: 800;
    text-decoration: none;
  }
  a:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.4); outline-offset: 3px; }
`;

const copy: Record<AdvancedCommerceState, { title: string; message: string }> = {
  checkout: { title: 'Secure checkout ready', message: 'Continue to Stripe to complete payment. Card details never pass through Davinci.' },
  reservation_unavailable: { title: 'Founding checkout is temporarily unavailable', message: 'All founding-price reservations are currently in progress. Please check again shortly; $500 is not offered until the first 25 paid assessments are settled.' },
  processing: { title: 'Confirming payment', message: 'We’re confirming your payment; do not pay again.' },
  cancelled: { title: 'No payment was completed', message: 'You can safely return to secure checkout when you are ready.' },
  expired: { title: 'This checkout link expired', message: 'No access was granted. Start a secure checkout to continue.' },
  failed: { title: 'Payment was not completed', message: 'Your assessment is still private and unchanged. You can try again or contact support.' },
  paid: { title: 'Access confirmed', message: 'Your payment has been verified. You can resume your assessment.' },
  refunded: { title: 'Refund recorded', message: 'Access and any unused credit follow the published refund policy. Support can verify your private order details.' },
  support_required: { title: 'We can verify this privately', message: 'This order needs a private review. Request a recovery link or contact support.' }
};

export interface AssessmentCommerceStatusProps {
  state: AdvancedCommerceState;
  /** GA4 `transaction_id` for the `purchase` event sent when the state is `paid`. */
  sessionId?: string;
  supportReference?: string;
  checking?: boolean;
  canCheck?: boolean;
  onCheck: () => void;
  onCheckout?: () => void;
  onResume?: () => void;
  onRecovery: () => void;
}

const AssessmentCommerceStatus: React.FC<AssessmentCommerceStatusProps> = ({
  state, sessionId, supportReference, checking = false, canCheck = true, onCheck, onCheckout, onResume, onRecovery
}) => {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [state]);
  useEffect(() => {
    if (state === 'paid' && sessionId) trackAdvancedPurchase(sessionId);
  }, [state, sessionId]);
  const content = copy[state];
  return (
    <Card aria-labelledby="commerce-status-heading">
      <h1 id="commerce-status-heading" tabIndex={-1} ref={heading}>{content.title}</h1>
      <p role="status" aria-live="polite">{content.message}</p>
      {supportReference && <p>Support reference: <strong>{supportReference}</strong></p>}
      <Actions>
        {state === 'paid' && onResume && <button type="button" onClick={onResume}>Resume assessment</button>}
        {['checkout', 'cancelled', 'expired', 'failed'].includes(state) && onCheckout && <button type="button" onClick={onCheckout}>Continue to secure checkout</button>}
        {['processing', 'reservation_unavailable'].includes(state) && <button type="button" onClick={onCheck} disabled={checking || !canCheck}>{checking ? 'Checking…' : 'Check status'}</button>}
        {state === 'support_required' && <button type="button" onClick={onRecovery}>Send recovery link</button>}
        {['processing', 'failed', 'refunded', 'support_required'].includes(state) && <a href="/contact">Contact support</a>}
      </Actions>
    </Card>
  );
};

export default AssessmentCommerceStatus;
